import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { Header } from '@widgets/header';
import { AppSidebar } from '@widgets/app-sidebar';
import { AuthLoading, useAuth } from '@features/auth';
import { useAbility } from '@features/authorization';
import { useUserProfileContext, type UserProfileContext } from '@entities/user-profile';
import {
  canAccessCrmCustomerDirectory,
  canAccessCrmCustomerMembershipDirectory,
  canAccessPersonalFlow,
} from '@shared/lib';
import {
  isValidLocale,
  syncLanguageFromUrl,
  DEFAULT_LOCALE,
  type SupportedLocale,
} from '@shared/config/i18n';
import { NotFoundPage } from '@pages/error';

/**
 * Locale Layout Route
 *
 * Handles /:locale/ URL structure
 * - Validates locale parameter (en, ru)
 * - Redirects invalid locales to /en/
 * - Syncs i18next language with URL locale
 * - Provides locale context to child routes
 * - Includes global navigation header (via Header widget)
 *
 * @updated DOC-0097 (2025-12-26) - Use Header widget from FSD widgets layer
 */

// Re-export locale type for backward compatibility
export type Locale = SupportedLocale;

function normalizePathname(pathname: string): string {
  return pathname.replace(/\/$/, '');
}

export function shouldResolveAuthenticatedHomeStart(input: {
  pathname: string;
  locale: string;
  isAuthenticated: boolean;
  isLoading: boolean;
}): boolean {
  return (
    normalizePathname(input.pathname) === `/${input.locale}` &&
    input.isAuthenticated &&
    !input.isLoading
  );
}

export function getAuthenticatedHomeCrmStart(
  stored: UserProfileContext['default_application_start_view'],
  access: { profiles: boolean; memberships: boolean; documents: boolean }
): UserProfileContext['default_application_start_view'] {
  if (stored?.route !== 'crm') return null;
  if (stored.tab === 'memberships') return access.memberships ? stored : null;
  if (!access.profiles) return null;
  if (stored.view === 'membership') return { route: 'crm', tab: 'profiles' };
  if (stored.view === 'documents' && !access.documents) return null;
  return stored;
}

export function getAuthenticatedHomeFallbackRoute(access: {
  dashboard: boolean;
  personalFlow: boolean;
}): '/$locale/team-dashboard' | '/$locale/student/personal-flow' | '/$locale/courses' {
  if (access.dashboard) return '/$locale/team-dashboard';
  if (access.personalFlow) return '/$locale/student/personal-flow';
  return '/$locale/courses';
}

export const Route = createFileRoute('/$locale')({
  beforeLoad: ({ params }) => {
    // Validate locale and redirect if invalid
    if (!isValidLocale(params.locale)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirect pattern
      throw redirect({ to: '/$locale', params: { locale: DEFAULT_LOCALE } });
    }

    // Sync i18next language with URL locale
    syncLanguageFromUrl(params.locale);
  },
  component: LocaleLayout,
  notFoundComponent: LocaleNotFoundPage,
});

function LocaleLayout() {
  const { locale } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();
  const ability = useAbility();
  const normalizedPath = normalizePathname(location.pathname);

  // Check if we're in a dashboard section (instructor, student, admin)
  // Dashboard sections have their own navigation
  const isDashboard =
    location.pathname.includes('/instructor') ||
    location.pathname.includes('/student') ||
    location.pathname.includes('/admin') ||
    location.pathname.includes('/sync') ||
    location.pathname.includes('/analytics') ||
    location.pathname.includes('/analytics-move') ||
    location.pathname.includes('/analytics-gear');

  const isHome = normalizedPath === `/${locale}`;
  const startContext = useUserProfileContext(
    user?.id ?? '',
    'me',
    isHome && isAuthenticated && !isLoading
  );
  const isLogin = normalizedPath.startsWith(`/${locale}/login`);
  const isRegister = normalizedPath.startsWith(`/${locale}/register`);
  const isSignup = normalizedPath.startsWith(`/${locale}/signup`);
  const isRecoveryLanding = normalizedPath.startsWith(`/${locale}/recovery/`);
  const isRecovery =
    isRecoveryLanding ||
    normalizedPath === `/${locale}/request-login-link` ||
    normalizedPath === `/${locale}/forgot-password`;
  const isInstructorGrowthLanding = normalizedPath.startsWith(
    `/${locale}/instructor-growth-operating-system`
  );
  const shouldShowSidebar = !(
    isHome ||
    isLogin ||
    isRegister ||
    isSignup ||
    isRecovery ||
    isInstructorGrowthLanding
  );
  const shouldHideDesktopHeader = shouldShowSidebar;

  useEffect(() => {
    if (
      !shouldResolveAuthenticatedHomeStart({
        pathname: location.pathname,
        locale,
        isAuthenticated,
        isLoading,
      })
    ) {
      return;
    }

    if (user !== null && startContext.isPending) return;

    const crmStart = getAuthenticatedHomeCrmStart(
      startContext.data?.data.default_application_start_view ?? null,
      {
        profiles: canAccessCrmCustomerDirectory(ability),
        memberships: canAccessCrmCustomerMembershipDirectory(ability),
        documents: ability.can('read', 'CrmCustomerDocumentMetadata'),
      }
    );
    if (crmStart !== null) {
      if (crmStart.tab === 'memberships') {
        void navigate({
          to: '/$locale/crm/memberships',
          params: { locale },
          replace: true,
        });
      } else {
        void navigate({
          to: '/$locale/crm',
          params: { locale },
          search: crmStart.view ? { view: crmStart.view } : {},
          replace: true,
        });
      }
      return;
    }

    void navigate({
      to: getAuthenticatedHomeFallbackRoute({
        dashboard: ability.can('manage', 'all') || ability.can('read', 'Dashboard'),
        personalFlow: canAccessPersonalFlow(ability, user?.id ?? null),
      }),
      params: { locale },
      replace: true,
    });
  }, [
    ability,
    isAuthenticated,
    isLoading,
    locale,
    location.pathname,
    navigate,
    startContext.data,
    startContext.isPending,
    user,
  ]);

  return (
    <div data-locale={locale} className="min-h-screen">
      {/* Global Header - hidden on desktop for sidebar layouts */}
      {!isDashboard && !isRecoveryLanding && (
        <Header locale={locale} className={shouldHideDesktopHeader ? 'min-[992px]:hidden' : ''} />
      )}

      <div
        className={`min-h-screen ${
          !isDashboard && !isRecoveryLanding
            ? shouldHideDesktopHeader
              ? 'pt-16 min-[992px]:pt-0'
              : 'pt-16'
            : ''
        }`}
      >
        <div
          className={`relative min-h-screen ${shouldShowSidebar ? 'min-[992px]:pl-[300px]' : ''}`}
        >
          {shouldShowSidebar && (
            <aside className="fixed left-0 top-0 hidden h-screen w-[300px] border-r border-border bg-card min-[992px]:block">
              <div className="h-full overflow-y-auto p-6">
                <AppSidebar locale={locale} />
              </div>
            </aside>
          )}
          {isHome && isAuthenticated && user !== null && startContext.isPending ? (
            <AuthLoading />
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  );
}

function LocaleNotFoundPage() {
  const { locale } = Route.useParams();
  return <NotFoundPage locale={locale} />;
}
